import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO — single component to manage page-level meta tags, OpenGraph/Twitter cards,
 * canonical URL, and optional JSON-LD structured data.
 *
 * Usage:
 *   <SEO title="BPC-157 · Research Compound" description="..." canonical="/product/bpc-157" />
 *
 *   <SEO ... structuredData={{ "@context": "https://schema.org", "@type": "Product", ... }} />
 */
const SITE_NAME = "Certified Wellness";
const DEFAULT_DESCRIPTION =
  "Certified Wellness — chemical-supplier of laboratory research peptides and blends. HPLC-tested, COA on every batch, made in the USA. For laboratory research use only.";
// Resolve canonical absolute URLs against the deployed origin (env override > window.location.origin).
const SITE_ORIGIN =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.REACT_APP_SITE_URL ||
  "https://certifiedwellness.net";

const absolute = (path) => {
  if (!path) return SITE_ORIGIN;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  image,
  type = "website",
  noindex = false,
  structuredData,
}) => {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Research-Grade Compounds`;
  const canonicalUrl = absolute(canonical || (typeof window !== "undefined" ? window.location.pathname : "/"));
  const ogImage = image ? absolute(image) : absolute("/api/static/images/c1sdcpud_CW_HERO_BAnner_COmpliant.png");

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* OpenGraph (Facebook, iMessage, LinkedIn, Slack) */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter / X cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
